import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config';
import html2pdf from 'html2pdf.js';
import {
    Container, Typography, Box, Button, CircularProgress, Alert, Paper, Grid, Chip, List, ListItem, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableHead, TableRow, Divider, FormControl, Select, MenuItem, InputLabel, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import BackButton from "./BackButton";
import { Download as DownloadIcon, CheckCircle as CheckCircleIcon, Info as InfoIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { alpha, useTheme } from '@mui/material/styles';

// Helper to determine chip color based on risk or priority
const getPriorityColor = (priority) => {
    if (!priority) return 'primary';
    const p = priority.toUpperCase();
    if (p.includes('LIKELY') || p.includes('HIGH') || p.includes('SIGNIFICANT')) return 'error';
    if (p.includes('EMERGING') || p.includes('MEDIUM') || p.includes('CO-OCCURRENCE')) return 'warning';
    if (p.includes('NO CONCERN') || p.includes('LOW') || p.includes('AGE APPROPRIATE')) return 'success';
    return 'primary';
};

const initialQuestionsEn = [
    // Section A: Social Communication (0-9)
    "Does your child make eye contact with you when interacting?",
    "Does your child smile in response to your smile or social cues? ",
    "Does your child point to indicate interest or to share?",
    "Can your child carry on a back-and-forth conversation?",
    "Does your child respond when their name is called?",
    "Does your child play pretend (e.g., tea party, superheroes)?",
    "Does your child show or bring objects to share with you?",
    "Does your child understand personal space and boundaries?",
    "Can your child adapt their behavior in different social settings?",
    "Does your child use gestures (e.g., waving, nodding)?",
    // Section B: Repetitive Behavior & Sensory Responses (10-17)
    "Does your child engage in repetitive movements (e.g., flapping, spinning)?",
    "Is your child unusually sensitive to noise, texture, or light?",
    "Does your child insist on routines or get upset with changes?",
    "Does your child show intense interest in a particular topic or object?",
    "Does your child line up objects or toys?",
    "Does your child echo words/phrases repeatedly (echolalia)?",
    "Does your child exhibit unusual attachment to objects (e.g., same toy)?",
    "Does your child get fixated on parts of objects?",
    // Section C: Inattention & Executive Function (18-24)
    "Does your child easily lose focus on tasks or activities?",
    "Does your child have trouble following instructions?",
    "Does your child frequently forget daily tasks (e.g., school bag)?",
    "Does your child avoid tasks that require effort (e.g., homework)?",
    "Does your child jump between activities without finishing?",
    "Does your child misplace items often?",
    "Does your child make careless mistakes due to inattention?",
    // Section D: Hyperactivity & Impulsivity (25-30)
    "Does your child fidget, squirm, or move constantly?",
    "Does your child run/climb in inappropriate settings?",
    "Does your child talk excessively?",
    "Does your child interrupt or intrude on others' conversations/games?",
    "Does your child blurt out answers or statements prematurely?",
    "Does your child have difficulty waiting their turn?",
    // Section E: Speech & Communication (31-46)
    "Does your child respond to his/her name?",
    "Does your child point to objects when asked?",
    "Does your child follow simple directions?",
    "Does your child get objects from another room when asked?",
    "Does your child point to body parts when asked?",
    "Does your child point to pictures in books when asked?",
    "Does your child answer simple questions?",
    "Does your child point to family members when asked?",
    "Does your child understand prepositions (e.g., in, on, under, next to)?",
    "Does your child understand color and size words (e.g., red, big, small)?",
    "Does your child engage in pretend/imaginary play?",
    "Does your child ignore you when you are speaking?",
    "Does your child socialize/play with other children?",
    "Do you or others have difficulty understanding your child's speech?",
    "Does your child appear frustrated when he/she is not understood?",
    "Does your child repeat sounds or words when speaking?"
];

const initialQuestionsTe = [
    // Section A: Social Communication (0-9)
    "మీరు పలకరించినప్పుడు మీ బిడ్డ కంటితో కలుపుతాడా?",
    "మీరు నవ్వినప్పుడు లేదా ఇతర సామాజిక సంకేతాలకు ప్రతిస్పందనగా మీ బిడ్డ నవ్వుతాడా?",
    "మీ బిడ్డ ఏదైనా ఆసక్తిని చూపించడానికి వేలు చూపిస్తాడా?",
    "మీ బిడ్డ మీతో పరస్పరం సంభాషించగలడా?",
    "పేరు పెట్టి పిలిచినప్పుడు మీ బిడ్డ స్పందిస్తాడా?",
    "మీ బిడ్డ ఏదైనా ఊహించుకుని ఆడుకుంటాడా?",
    "మీ బిడ్డ వస్తువులను తీసుకువచ్చి మీతో పంచుకుంటాడా?",
    "మీ బిడ్డ వ్యక్తిగత స్థల పరిమితులను అర్థం చేసుకుంటాడా?",
    "వివిధ సామాజిక వాతావరణాల్లో మీ బిడ్డ తన ప్రవర్తనను మార్చుకుంటాడా?",
    "మీ బిడ్డ హావభావాలను ఉపయోగిస్తాడా?",
    // Section B: Repetitive Behavior & Sensory Responses (10-17)
    "మీ బిడ్డ మళ్లీ మళ్లీ ఒకేలాంటి కదలికలు చేస్తాడా?",
    "ధ్వని లేదా ఉష్ణోగ్రతలకు మీ బిడ్డ అసాధారణంగా స్పందిస్తాడా?",
    "సాధారణ దినచర్య మారితే మీ బిడ్డ కోపగించుకుంటాడా?",
    "ఏదో ఒక వస్తువు లేదా విషయంపై విపరీతమైన ఆసక్తి చూపిస్తాడా?",
    "వస్తువులను వరుసలో పేర్చుతాడా?",
    "ఒకే పదం లేదా వాక్యాన్ని మళ్లీ మళ్లీ అంటాడా?",
    "సాధారణ వస్తువులపట్ల (ఉదాహరణకు ఒకే బొమ్మ) అసాధారణ ప్రేమ చూపిస్తాడా?",
    "వస్తువుల్లోని భాగాలపై ఎక్కువ ఆసక్తి చూపిస్తాడా?",
    // Section C: Inattention & Executive Function (18-24)
    "పనులలో మీ బిడ్డ త్వరగా శ్రద్ధ కోల్పోతాడా?",
    "మీరు చెప్పిన సూచనలు పాటించడంలో ఇబ్బంది పడతాడా?",
    "రోజువారీ పనులను మర్చిపోతాడా?",
    "ఎక్కువ శ్రమ అవసరమయ్యే పనులను వాయిదా వేస్తాడా లేదా తప్పించుకుంటాడా?",
    "ఒక పని పూర్తి కాకుండానే ఇంకో పని మొదలుపెడతాడా?",
    "వస్తువులను తరచుగా పోగొట్టుకుంటాడా?",
    "శ్రద్ధ లోపం వల్ల పదే పదే తప్పులు చేస్తాడా?",
    // Section D: Hyperactivity & Impulsivity (25-30)
    "మీ బిడ్డ ఎప్పుడూ కదులుతూ ఉంటాడా లేదా స్థిరంగా ఉండలేడా?",
    "తగని ప్రదేశాలలో పరిగెత్తడం లేదా ఎక్కడం చేస్తాడా?",
    "మీ బిడ్డ ఎక్కువగా మాట్లాడతాడా?",
    "ఇతరుల సంభాషణలు లేదా ఆటలకు అంతరాయం కలిగిస్తాడా?",
    "ప్రశ్న పూర్తి కాకుండానే సమాధానం చెబుతాడా?",
    "తన వంతు వచ్చే వరకు ఎదురుచూడలేడా?",
    // Section E: Speech & Communication (31-46)
    "తన పేరుకు స్పందిస్తారా?",
    "అడిగినప్పుడు వస్తువులను చూపిస్తారా?",
    "సాధారణ సూచనలను పాటిస్తారా?",
    "అడిగినప్పుడు వేరే గది నుండి వస్తువులను తెస్తారా?",
    "అడిగినప్పుడు శరీర భాగాలను చూపిస్తారా?",
    "అడిగినప్పుడు పుస్తకాలలోని చిత్రాలను చూపిస్తారా?",
    "సాధారణ ప్రశ్నలకు సమాధానం ఇస్తారా?",
    "అడిగినప్పుడు కుటుంబ సభ్యులను చూపిస్తారా?",
    "ప్రిపోజిషన్లు (ఉదా. లో, మీద, కింద, పక్కన) అర్థం చేసుకుంటారా?",
    "రంగు మరియు పరిమాణ పదాలను (ఉదా. ఎరుపు, పెద్దది, చిన్నది) అర్థం చేసుకుంటారా?",
    "నటించే/ఊహాత్మక ఆటలలో పాల్గొంటారా?",
    "మీరు మాట్లాడుతున్నప్పుడు మీ బిడ్డ మిమ్మల్ని నిర్లక్ష్యం చేస్తారా?",
    "మీ బిడ్డ ఇతర పిల్లలతో కలిసి మెలిసి ఉంటారా/ఆడుకుంటారా?",
    "మీ బిడ్డ మాటలు అర్థం చేసుకోవడంలో మీకు లేదా ఇతరులకు ఇబ్బంది ఉందా?",
    "తాను చెప్పేది అర్థం కానప్పుడు మీ బిడ్డ నిరాశకు గురవుతారా?",
    "మాట్లాడేటప్పుడు మీ బిడ్డ శబ్దాలను లేదా పదాలను పునరావృతం చేస్తారా?"
];

const answerLabelsEn = ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'];
const answerLabelsTe = ['ఎప్పుడూ లేదు', 'అరుదుగా', 'కొన్నిసార్లు', 'తరచుగా', 'ఎల్లప్పుడూ'];

const Dictionary = {
    "Clinical Assessment Report": { en: "Clinical Assessment Report", te: "క్లినికల్ అసెస్‌మెంట్ రిపోర్ట్" },
    "Generated on": { en: "Generated on", te: "సృష్టించబడిన తేదీ" },
    "Child Information": { en: "Child Information", te: "పిల్లల సమాచారం" },
    "Name": { en: "Name", te: "పేరు" },
    "Latest Assessment Date": { en: "Latest Assessment Date", te: "తాజా అంచనా తేదీ" },
    "Joining Date": { en: "Joining Date", te: "చేరిన తేదీ" },
    "Assessed by": { en: "Assessed by", te: "అంచనా వేసిన వారు" },
    "Latest Result": { en: "Latest Result", te: "తాజా ఫలితం" },
    "Speech Therapist": { en: "Speech Therapist", te: "స్పీచ్ థెరపిస్ట్" },
    "OT Specialist": { en: "OT Specialist", te: "ఒకటో స్పెషలిస్ట్" },
    "Clinical Team": { en: "Clinical Team", te: "క్లినికల్ బృందం" },
    "Executive Summary": { en: "Executive Summary", te: "కార్యనిర్వాహక సారాంశం" },
    "Target": { en: "Target", te: "లక్ష్యం" },
    "Outcome": { en: "Outcome", te: "ఫలితం" },
    "Social Interaction & Compliance": { en: "Social Interaction & Compliance", te: "సామాజిక పరస్పర చర్య & సమ్మతి" },
    "Fine Motor & Sensory Regulation": { en: "Fine Motor & Sensory Regulation", te: "చక్కటి మోటార్ & ఇంద్రియ నియంత్రణ" },
    "Functional Communication": { en: "Functional Communication", te: "క్రియాత్మక కమ్యూనికేషన్" },
    "General Developmental Milestones": { en: "General Developmental Milestones", te: "సాధారణ అభివృద్ధి మైలురాళ్ళు" },
    "Behavioral Independence": { en: "Behavioral Independence", te: "ప్రవర్తనా స్వాతంత్ర్యం" },
    "ADL Independence": { en: "ADL Independence", te: "ADL స్వాతంత్ర్యం" },
    "Spontaneous Dialogue": { en: "Spontaneous Dialogue", te: "సహజ సంభాషణ" },
    "Overall Developmental Alignment": { en: "Overall Developmental Alignment", te: "మొత్తం అభివృద్ధి అమరిక" },
    "Occupational Interventions": { en: "Occupational Interventions", te: "ఆక్యుపేషనల్ జోక్యాలు" },
    "Behavioral Support": { en: "Behavioral Support", te: "ప్రవర్తనా మద్దతు" },
    "Speech & Language": { en: "Speech & Language", te: "స్పీచ్ & లాంగ్వేజ్" },
    "Parent Training and Support": { en: "Parent Training and Support", te: "తల్లిదండ్రుల శిక్షణ మరియు మద్దతు" },
    "Increase turn-taking and response to name": { en: "Increase turn-taking and response to name", te: "వంతులు వేసుకోవడాన్ని మరియు పేరు పలికినప్పుడు ప్రతిస్పందనను పెంచడం" },
    "Follow 2-step instructions consistently": { en: "Follow 2-step instructions consistently", te: "2-దశల సూచనలను నిలకడగా పాటించడం" },
    "Improve pincer grasp and sitting tolerance": { en: "Improve pincer grasp and sitting tolerance", te: "పించర్ పట్టును మరియు కూర్చునే శక్తిని మెరుగుపరచడం" },
    "Write/Draw for 10 mins with focused attention": { en: "Write/Draw for 10 mins with focused attention", te: "10 నిమిషాల పాటు ఏకాగ్రతతో వ్రాయడం/గీయడం" },
    "Reduce echolalia and use expressive words": { en: "Reduce echolalia and use expressive words", te: "ఎకోలాలియాను (మాటల పునరావృతం) తగ్గించడం మరియు భావ వ్యక్తీకరణ పదాలను ఉపయోగించడం" },
    "Communicate needs using 3-word sentences": { en: "Communicate needs using 3-word sentences", te: "3-పదాల వాక్యాలను ఉపయోగించి అవసరాలను కమ్యూనికేట్ చేయడం" },
    "Consistent performance in observed domains": { en: "Consistent performance in observed domains", te: "గమనించిన రంగాలలో నిలకడగా పనితీరు" },
    "Achieve emerging score levels": { en: "Achieve emerging score levels", te: "ఎదుగుతున్న స్కోర్ స్థాయిలను సాధించడం" },
    "Function in group settings without one-on-one support": { en: "Function in group settings without one-on-one support", te: "ఒకరికొకరు మద్దతు లేకుండా సమూహ సెట్టింగ్‌లలో పాల్గొనడం" },
    "Socially appropriate behavior in variety of settings": { en: "Socially appropriate behavior in variety of settings", te: "వివిధ సెట్టింగ్‌లలో సామాజికంగా తగిన ప్రవర్తన" },
    "Complete all self-care tasks independently": { en: "Complete all self-care tasks independently", te: "అన్ని స్వయంసంరక్షణ పనులను స్వతంత్రంగా పూర్తి చేయడం" },
    "Master fine motor control at age-appropriate level": { en: "Master fine motor control at age-appropriate level", te: "వయస్సుకు తగిన స్థాయిలో చక్కటి మోటార్ నియంత్రణను సాధించడం" },
    "Carry out complex conversations naturally": { en: "Carry out complex conversations naturally", te: "సంక్లిష్టమైన సంభాషణలను సహజంగా జరపడం" },
    "Effective social communication for academic/social success": { en: "Effective social communication for academic/social success", te: "అకడమిక్/సామాజిక విజయానికి సమర్థవంతమైన సామాజిక కమ్యూనికేషన్" },
    "Perform tasks independently at age level": { en: "Perform tasks independently at age level", te: "వయస్సు స్థాయిలో పనులను స్వతంత్రంగా చేయడం" },
    "Function in mainstream environments": { en: "Function in mainstream environments", te: "ప్రధాన స్రవంతి వాతావరణాలలో పాల్గొనడం" },
    "Sensory integration activities": { en: "Sensory integration activities", te: "ఇంద్రియ ఏకీకరణ కార్యకలాపాలు" },
    "Targeted fine and gross motor skill development": { en: "Targeted fine and gross motor skill development", te: "లక్ష్యంగా చేసుకున్న చక్కటి మరియు స్థూల మోటార్ స్కిల్ అభివృద్ధి" },
    "Self-care independence practice": { en: "Self-care independence practice", te: "స్వయంసంరక్షణ స్వాతంత్ర్య అభ్యాసం" },
    "Positive reinforcement schedules": { en: "Positive reinforcement schedules", te: "సానుకూల ఉపబల షెడ్యూల్‌లు" },
    "Social skills training groups": { en: "Social skills training groups", te: "సామాజిక నైపుణ్యాల శిక్షణ సమూహాలు" },
    "Environmental modifications for stability": { en: "Environmental modifications for stability", te: "స్థిరత్వం కోసం పర్యావరణ మార్పులు" },
    "Functional communication training": { en: "Functional communication training", te: "ఫంక్షనల్ కమ్యూనికేషన్ శిక్షణ" },
    "Joint attention exercises": { en: "Joint attention exercises", te: "సమ్మిళిత ఏకాగ్రత వ్యాయామాలు" },
    "Augmentative communication if necessary": { en: "Augmentative communication if necessary", te: "అవసరమైతే ఆగ్మెంటేటివ్ కమ్యూనికేషన్" },
    "Learn interactive reinforcement": { en: "Learn interactive reinforcement", te: "ఇంటరాక్టివ్ ఉపబలాలను నేర్చుకోవడం" },
    "Implement structured routines at home": { en: "Implement structured routines at home", te: "ఇంట్లో నిర్మాణాత్మక నిత్యకృత్యాలను అమలు చేయడం" },
    "Engage in parent-coached play sessions": { en: "Engage in parent-coached play sessions", te: "తల్లిదండ్రుల-కోచ్ చేసిన ఆట సెషన్లలో పాల్గొనడం" },
    "Initial Assessment Questionnaire": { en: "Initial Assessment Questionnaire", te: "మొదటి అంచనా ప్రశ్నలు" },
    "Question": { en: "Question", te: "ప్రశ్న" },
    "Answer": { en: "Answer", te: "సమాధానం" },
    "Assessment Scoring History": { en: "Assessment Scoring History", te: "అంచనా స్కోరింగ్ చరిత్ర" },
    "Goals & Objectives": { en: "Goals & Objectives", te: "లక్ష్యాలు & ఉద్దేశ్యాలు" },
    "Recommendations": { en: "Recommendations", te: "సిఫార్సులు" },
    "Clinical Team / OT Specialist": { en: "Clinical Team / OT Specialist", te: "క్లినికల్ బృందం / ఒకటో స్పెషలిస్ట్" },
    "Occupational Therapy Reassessment Details": { en: "Occupational Therapy Reassessment Details", te: "ఆక్యుపేషనల్ థెరపీ రీఅసెస్మెంట్ వివరాలు" },
    "Physical & Postural Assessment": { en: "Physical & Postural Assessment", te: "శారీరక & భంగిమ అంచనా" },
    "Fine Motor & Gross Motor": { en: "Fine Motor & Gross Motor", te: "నిర్దిష్ట మరియు స్థూల మోటార్ నైపుణ్యాలు" },
    "Hand Function": { en: "Hand Function", te: "చేతి పనితీరు" },
    "Positions & Transitions": { en: "Positions & Transitions", te: "స్థానాలు & పరివర్తనాలు" },
    "Behavioral & Attention": { en: "Behavioral & Attention", te: "ప్రవర్తన & శ్రద్ధ" },
    "Self Care & Daily Living": { en: "Self Care & Daily Living", te: "స్వీయ జాగ్రత్త & రోజువారీ జీవనం" },
    "Short-term Goals": { en: "Short-term Goals (3 months)", te: "స్వల్పకాలిక లక్ష్యాలు (3 నెలలు)" },
    "Long-term Goals": { en: "Long-term Goals (12 months)", te: "దీర్ఘకాలిక లక్ష్యాలు (12 నెలలు)" },
    "Ongoing Specialized Therapy": { en: "Ongoing Specialized Therapy", te: "కొనసాగుతున్న ప్రత్యేక చికిత్స" },
    "Occupational Interventions": { en: "Occupational Interventions", te: "వృత్తిపరమైన జోక్యాలు" },
    "Parent Training and Support": { en: "Parent Training and Support", te: "తల్లిదండ్రుల శిక్షణ మరియు మద్దతు" },
    "Comprehensive Developmental Strategy & Road Map": { en: "Comprehensive Developmental Strategy & Road Map", te: "సమగ్ర అభివృద్ధి వ్యూహం & రోడ్ మ్యాప్" },
    "Detailed Overview & Next Steps": { en: "Detailed Overview & Next Steps", te: "వివరణాత్మక అవలోకనం & తదుపరి దశలు" },
    "Assessment Summary & Risk Profile": { en: "Assessment Summary & Risk Profile", te: "అంచనా సారాంశం & రిస్క్ ప్రొఫైల్" },
    "Autism Probability Index": { en: "Autism Probability Index", te: "ఆటిజం ప్రాబబిలిటీ ఇండెక్స్" },
    "Domain Specific Analysis": { en: "Domain Specific Analysis", te: "డొమైన్ వారీ విశ్లేషణ" },
    "SRS SCORE": { en: "SRS SCORE", te: "SRS స్కోర్" },
    "M-CHAT-R SCORE": { en: "M-CHAT-R SCORE", te: "M-CHAT-R స్కోర్" },
    "INTERPRETATION": { en: "INTERPRETATION", te: "ఫలితం / వివరణ" },
    "Findings Overview": { en: "Findings Overview", te: "కనుగొన్న అంశాల అవలోకనం" },
    "Social": { en: "Social", te: "సామాజిక" },
    "Repetitive": { en: "Repetitive", te: "పునరావృత" },
    "Inattention": { en: "Inattention", te: "అశ్రద్ధ" },
    "Hyperactivity": { en: "Hyperactivity", te: "అతి చురుకుదనం" },
    "Speech": { en: "Speech & Comm.", te: "స్పీచ్ & కమ్యూనికేషన్" },
    "Behavioral Reassessment Details": { en: "Behavioral Reassessment Details", te: "ప్రవర్తనా పునర్జన్మ వివరాలు" },
    "Attention & Compliance": { en: "Attention & Compliance", te: "శ్రద్ధ & విధేయత" },
    "Emotional & Behavioral Control": { en: "Emotional & Behavioral Control", te: "భావోద్వేగ & ప్రవర్తనా నియంత్రణ" },
    "Sensory & Repetitive": { en: "Sensory & Repetitive", te: "జ్ఞానేంద్రియ & పునరావృత" },
    "Social & Transitions": { en: "Social & Transitions", te: "సామాజిక & పరివర్తనాలు" },
    "Tact Skills (Labeling) Progress": { en: "Tact Skills (Labeling) Progress", te: "టాక్ట్ నైపుణ్యాలు (లేబులింగ్) పురోగతి" },
    "Imitation & Play": { en: "Imitation & Play", te: "అనుకరణ & ఆట" },
    "Communication": { en: "Communication", te: "కమ్యూనికేషన్" },
    "Clinical Summaries": { en: "Clinical Summaries", te: "క్లినికల్ సారాంశాలు" },
    "Sustains Attention": { en: "Sustains Attention", te: "శ్రద్ధను కొనసాగిస్తుంది" },
    "Sitting Tolerance": { en: "Sitting Tolerance", te: "కూర్చునే ఓపిక" },
    "Follows Instructions": { en: "Follows Instructions", te: "సూచనలను పాటిస్తుంది" },
    "Emotional Regulation": { en: "Emotional Regulation", te: "భావోద్వేగ నియంత్రణ" },
    "Aggressive Behavior": { en: "Aggressive Behavior", te: "దురహంకార ప్రవర్తన" },
    "Self-Harm": { en: "Self-Harm", te: "స్వీయ హాని" },
    "Repetitive Behavior": { en: "Repetitive Behavior", te: "పునరావృత ప్రవర్తన" },
    "Sensory (Sound/Touch)": { en: "Sensory (Sound/Touch)", te: "జ్ఞానేంద్రియ (ధ్వని/స్పర్శ)" },
    "Transitions": { en: "Transitions", te: "పరివర్తనాలు" },
    "Imitation": { en: "Imitation", te: "అనుకరణ" },
    "Play": { en: "Play", te: "ఆట" },
    "Comprehension": { en: "Comprehension", te: "అవగాహన" },
    "Expression": { en: "Expression", te: "వ్యక్తీకరణ" },
    "Objects": { en: "Objects", te: "వస్తువులు" },
    "Actions": { en: "Actions", te: "పనులు" },
    "Categories": { en: "Categories", te: "వర్గాలు" },
    "Colors": { en: "Colors", te: "రంగులు" },
    "Academic": { en: "Academic", te: "చదువు" },
    "Common": { en: "Common", te: "సాధారణ" },
    "Personal": { en: "Personal", te: "వ్యక్తిగత" },
    "Classroom": { en: "Classroom", te: "తరగతి గది" },
    "Household": { en: "Household", te: "ఇల్లు" },
    "Toys": { en: "Toys", te: "బొమ్మలు" },
    "Alphabet": { en: "Alphabet", te: "వర్ణమాల" },
    "Numbers": { en: "Numbers", te: "సంఖ్యలు" },
    "Telugu": { en: "Telugu", te: "తెలుగు" },
    "Shapes": { en: "Shapes", te: "ఆకారాలు" },
    "Never": { en: "Never", te: "ఎప్పుడూ లేదు" },
    "Rarely": { en: "Rarely", te: "అరుదుగా" },
    "Sometimes": { en: "Sometimes", te: "కొన్నిసార్లు" },
    "Often": { en: "Often", te: "తరచుగా" },
    "Always": { en: "Always", te: "ఎల్లప్పుడూ" },
    "Frequent": { en: "Frequent", te: "తరచుగా" },
    "Occasional": { en: "Occasional", te: "అప్పుడప్పుడు" },
    "Rare": { en: "Rare", te: "అరుదుగా" },
    "Not Seen": { en: "Not Seen", te: "చూడలేదు" },
    "Typical": { en: "Typical", te: "సాధారణ" },
    "Typical(Normal)": { en: "Typical (Normal)", te: "సాధారణ (నార్మల్)" },
    "Over-responsive": { en: "Over-responsive", te: "అతిగా స్పందించడం" },
    "Under-responsive": { en: "Under-responsive", te: "తక్కువగా స్పందించడం" },
    "Present": { en: "Present", te: "ఉంది" },
    "Absent": { en: "Absent", te: "లేదు" },
    "Consistent": { en: "Consistent", te: "స్థిరమైన" },
    "Inconsistent": { en: "Inconsistent", te: "అస్థిరమైన" },
    "Prompt based": { en: "Prompt based", te: "ప్రేరేపిత ఆధారిత" },
    "Observed": { en: "Observed", te: "గమనించబడింది" },
    "Not observed": { en: "Not observed", te: "గమనించబడలేదు" },
    "Emerging with person": { en: "Emerging", te: "అలవడుతోంది" },
    "Minimal to No Signs": { en: "Minimal to No Signs", te: "చాలా తక్కువ లేదా ఏమీ లేవు" },
    "Mild to Moderate Signs": { en: "Mild to Moderate Signs", te: "తక్కువ నుండి మోస్తరు లక్షణాలు" },
    "Significant Signs - Further Eval": { en: "Significant Signs - Further Eval", te: "గణనీయమైన లక్షణాలు - తదుపరి మూల్యాంకనం" },
    "Speech & Language Re-assessment Summary": { en: "Speech & Language Re-assessment Summary", te: "స్పీచ్ & లాంగ్వేజ్ రీ-అసెస్మెంట్ సారాంశం" },
    "TOTAL SCORE": { en: "TOTAL SCORE", te: "మొత్తం స్కోర్" },
    "Improvement": { en: "Improvement", te: "మెరుగుదల" },
    "Clinical Interpretation": { en: "Clinical Interpretation", te: "క్లినికల్ వివరణ" },
    "Domain Detailed Scoring": { en: "Domain Detailed Scoring", te: "డొమైన్ వారీ వివరణాత్మక స్కోరింగ్" },
    "Therapy Goals & Home Plan": { en: "Therapy Goals & Home Plan", te: "థెరపీ లక్ష్యాలు & ఇంటి ప్రణాళిక" },
    "Short-Term Goals:": { en: "Short-Term Goals:", te: "స్వల్పకాలిక లక్ష్యాలు:" },
    "Long-Term Goals:": { en: "Long-Term Goals:", te: "దీర్ఘకాలిక లక్ష్యాలు:" },
    "Recommended Activities:": { en: "Recommended Activities:", te: "సిఫార్సు చేసిన పనులు:" },
    "Frequency:": { en: "Frequency:", te: "తరచుదనం:" },
    "Initial Diagnostic Baseline": { en: "Initial Diagnostic Baseline", te: "ప్రారంభ రోగనిర్ధారణ బేస్లైన్" },
    "INITIAL INTERPRETATION": { en: "INITIAL INTERPRETATION", te: "ప్రారంభ వివరణ" },
    "Age": { en: "Age", te: "వయస్సు" },
    "Gender": { en: "Gender", te: "లింగం" },
    "Summary of Needs": { en: "Summary of Needs", te: "అవసరాల సారాంశం" },
    "Strengths": { en: "Strengths", te: "బలాలు" },
    "Assessment results inform the targeted therapy": { en: "Assessment results inform the targeted therapy", te: "అంచనా ఫలితాలు లక్షిత చికిత్సను తెలియజేస్తాయి" },
    "Target Outcome": { en: "Target Outcome", te: "లక్షిత ఫలితం" },
    "Status": { en: "Status", te: "స్థితి" },
    "Achieved": { en: "Achieved", te: "సాధించారు" },
    "In Progress": { en: "In Progress", te: "పురోగతిలో ఉంది" },
    "Pending": { en: "Pending", te: "పెండింగ్‌లో ఉంది" },
    "Action Item": { en: "Action Item", te: "చేయవలసిన పని" },
    "Priority": { en: "Priority", te: "ప్రాధాన్యత" },
    "High": { en: "High", te: "ఎక్కువ" },
    "Medium": { en: "Medium", te: "మధ్యస్థం" },
    "Low": { en: "Low", te: "తక్కువ" },
    "mins": { en: "mins", te: "నిమిషాలు" },
    "Simple": { en: "Simple", te: "సులభమైనవి" },
    "2-Step": { en: "2-Step", te: "2-దశలవి" },
    "Tantrums": { en: "Tantrums", te: "కోపతాపాలు" },
    "Crying": { en: "Crying", te: "ఏడుపు" },
    "Hits/Kicks": { en: "Hits/Kicks", te: "కొట్టడం/తన్నడం" },
    "Biting": { en: "Biting", te: "కరవడం" },
    "Flapping": { en: "Flapping", te: "రెక్కలు ఆడించడం" },
    "Stemming": { en: "Stemming", te: "పునరావృత శబ్దాలు" },
    "Sound": { en: "Sound", te: "శబ్దం" },
    "Touch": { en: "Touch", te: "స్పర్శ" },
    "Eye Contact": { en: "Eye Contact", te: "కళ్ళలోకి చూడటం" },
    "Name": { en: "Name", te: "పేరు" },
    "Running": { en: "Running", te: "పరుగెత్తడం" },
    "Eating": { en: "Eating", te: "తినడం" },
    "Sleeping": { en: "Sleeping", te: "నిద్రించడం" },
    "Jumping": { en: "Jumping", te: "దూకడం" },
    "Playing": { en: "Playing", te: "ఆడుకోవడం" },
    "Sitting": { en: "Sitting", te: "కూర్చోవడం" },
    "Animals": { en: "Animals", te: "జంతువులు" },
    "Fruits": { en: "Fruits", te: "పండ్లు" },
    "Vegetables": { en: "Vegetables", te: "కూరగాయలు" },
    "Occupation": { en: "Occupation", te: "వృత్తులు" },
    "Body Parts": { en: "Body Parts", te: "శరీర భాగాలు" },
    "Vehicle": { en: "Vehicle", te: "వాహనం" },
    "Red": { en: "Red", te: "ఎరుపు" },
    "Blue": { en: "Blue", te: "నీలం" },
    "Green": { en: "Green", te: "ఆకుపచ్చ" },
    "Yellow": { en: "Yellow", te: "పసుపు" },
    "Other": { en: "Other", te: "ఇతర" },
    "Non-Prehensile": { en: "Non-Prehensile", te: "నాన్-ప్రిహెన్సైల్ (పట్టుకోలేని)" },
    "Pushing": { en: "Pushing", te: "నెట్టడం" },
    "Pulling": { en: "Pulling", te: "లాగడం" },
    "Clapping": { en: "Clapping", te: "చప్పట్లు కొట్టడం" },
    "Prehensile (Grasps)": { en: "Prehensile (Grasps)", te: "ప్రిహెన్సైల్ (పట్టుకోవడం)" },
    "Cylindrical": { en: "Cylindrical", te: "సిలిండ్రికల్" },
    "Spherical": { en: "Spherical", te: "స్ఫెరికల్" },
    "Pincer": { en: "Pincer", te: "పించర్" },
    "Beading / Scissoring": { en: "Beading / Scissoring", te: "పూసలు గ్రుచ్చడం / కత్తిరించడం" },
    "Beading": { en: "Beading", te: "పూసలు గ్రుచ్చడం" },
    "Scissoring": { en: "Scissoring", te: "కత్తిరించడం" },
    "Rt": { en: "Rt", te: "కుడి" },
    "Lf": { en: "Lf", te: "ఎడమ" },
    "Command Following": { en: "Command Following", te: "సూచనలను పాటించడం" },
    "Span of Reaction": { en: "Span of Reaction", te: "ప్రతిచర్య వ్యవధి" },
    "Attention": { en: "Attention", te: "శ్రద్ధ" },
    "Hyperactivity": { en: "Hyperactivity", te: "అతి చురుకుదనం" },
    "Verbal Stimming": { en: "Verbal Stimming", te: "పునరావృత మాటలు" },
    "Walking / Running": { en: "Walking / Running", te: "నడక / పరుగు" },
    "Standing (One Leg)": { en: "Standing (One Leg)", te: "నిలబడటం (ఒక కాలిపై)" },
    "Stairs / Balance Beam": { en: "Stairs / Balance Beam", te: "మెట్లు / బ్యాలెన్స్ బీమ్" },
    "Stairs": { en: "Stairs", te: "మెట్లు" },
    "Beam": { en: "Beam", te: "బీమ్" },
    "Ball Skills": { en: "Ball Skills", te: "బంతి ఆట నైపుణ్యాలు" },
    "Catch": { en: "Catch", te: "పట్టుకోవడం" },
    "Over Head": { en: "Over Head", te: "తల పైనుండి" },
    "Ride Cycle": { en: "Ride Cycle", te: "సైకిల్ తొక్కడం" },
    "Self Care": { en: "Self Care", te: "స్వీయ జాగ్రత్త" },
    "Eating": { en: "Eating", te: "తినడం" },
    "Bathing": { en: "Bathing", te: "స్నానం చేయడం" },
    "Dressing (Upper / Lower)": { en: "Dressing (Upper / Lower)", te: "దుస్తులు ధరించడం (పైన / కింద)" },
    "Upper": { en: "Upper", te: "పైన" },
    "Lower": { en: "Lower", te: "కింద" },
    "Toileting / Buttoning": { en: "Toileting / Buttoning", te: "టాయిలెటింగ్ / బటన్ వేసుకోవడం" },
    "Toileting": { en: "Toileting", te: "టాయిలెటింగ్" },
    "Buttoning": { en: "Buttoning", te: "బటన్ వేసుకోవడం" },
    "Writing Skills": { en: "Writing Skills", te: "రాత నైపుణ్యాలు" },
    "Grips": { en: "Grips", te: "పట్టు" },
    "Pincer Grip": { en: "Pincer Grip", te: "పించర్ గ్రిప్" },
    "Finger": { en: "Finger", te: "వేలు" },
    "Alphabets": { en: "Alphabets", te: "అక్షరాలు" },
    "Preferences": { en: "Preferences", te: "అభిరుచులు" },
    "Food": { en: "Food", te: "ఆహారం" },
    "Sensory": { en: "Sensory", te: "జ్ఞానేంద్రియ" }
};

const ChildReport = () => {
    const { t } = useTranslation();
    const { childId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const assessmentIdParam = searchParams.get('assessmentId');
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reportLang, setReportLang] = useState('en');
    const reportRef = useRef();
    const theme = useTheme();

    useEffect(() => {
        const fetchReportData = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/get_child_report_data.php?child_id=${childId}`);
                setReportData(response.data);
            } catch (err) {
                setError(t('error_fetching_report_data'));
            } finally {
                setLoading(false);
            }
        };
        fetchReportData();
    }, [childId, t]);

    const generatePdf = () => {
        const element = reportRef.current;
        const opt = {
            margin:       [0.5, 0.5, 0.5, 0.5],
            filename:     `assessment_report_${reportData?.child_info?.name?.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2, useCORS: true },
            jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait' }
        };
        html2pdf().from(element).set(opt).save();
    };

    const lx = (key) => {
        if (!key) return "";
        if (!Dictionary[key]) {
            // Check for case-insensitive match or common value mapping
            const found = Object.keys(Dictionary).find(k => k.toLowerCase() === String(key).toLowerCase());
            if (found) return Dictionary[found][reportLang] || Dictionary[found]['en'];
            return key;
        }
        return Dictionary[key][reportLang] || Dictionary[key]['en'];
    }

    const vt = (val) => {
        if (!val) return "N/A";
        return lx(val);
    };

    if (loading) return <Container sx={{ textAlign: 'center', mt: 5 }}><CircularProgress /></Container>;
    if (error) return <Container sx={{ mt: 5 }}><Alert severity="error">{error}</Alert></Container>;
    if (!reportData?.child_info) return <Container sx={{ mt: 5 }}><Alert severity="info">{t('no_data_available')}</Alert></Container>;

    const { child_info, assessments } = reportData;
    let selectedAssessment = assessments?.[0] || {}; 
    if (assessmentIdParam && assessments) {
        const found = assessments.find(a => String(a.id) === assessmentIdParam);
        if (found) {
            selectedAssessment = found;
        }
    }
    const latestAssessment = selectedAssessment; 
    
    // The very first assessment (to include in the report)
    const firstAssessment = assessments && assessments.length > 0 ? assessments[assessments.length - 1] : null;

    // Helper to safely parse JSON from DB
    const extractAnswers = (ans) => {
        if (!ans) return null;
        if (typeof ans === 'string') {
            try { return JSON.parse(ans); } catch (e) { return null; }
        }
        return ans;
    };

    const latestAnswers = extractAnswers(latestAssessment.answers);
    const firstAnswers = extractAnswers(firstAssessment?.answers);
    const isOTLatest = latestAssessment.assessment_type === "ot_reassessment";
    const isSpeechLatest = latestAssessment.assessment_type === "speech_reassessment";
    const isBehavioralLatest = latestAssessment.assessment_type === "behavioral_reassessment";
    const isInitialLatest = latestAssessment.assessment_type === "initial";

    const questionsList = reportLang === 'te' ? initialQuestionsTe : initialQuestionsEn;
    const answerLabelsList = reportLang === 'te' ? answerLabelsTe : answerLabelsEn;

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, bgcolor: '#f4f6f8' }}>
            <BackButton />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 4 }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: assessments && assessments.length > 1 ? 1 : 0 }}>
                        {t('child_report_title')}
                    </Typography>
                    {assessments && assessments.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 250, bgcolor: 'white', borderRadius: 1 }}>
                            <Select
                                value={latestAssessment.id || ''}
                                onChange={(e) => setSearchParams({ assessmentId: e.target.value })}
                            >
                                {assessments.map(a => (
                                    <MenuItem key={a.id} value={a.id}>
                                        {a.assessment_type === 'ot_reassessment' ? 'Occupational Therapy' : 
                                         a.assessment_type === 'speech_reassessment' ? 'Speech Therapy' : 'Autism & ADHD'} - {new Date(a.created_at).toLocaleDateString()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl component="fieldset">
                        <RadioGroup row value={reportLang} onChange={(e) => setReportLang(e.target.value)}>
                            <FormControlLabel value="en" control={<Radio color="primary" />} label="English" />
                            <FormControlLabel value="te" control={<Radio color="primary" />} label="Telugu / తెలుగు" />
                        </RadioGroup>
                    </FormControl>
                    <Button variant="contained" onClick={generatePdf} startIcon={<DownloadIcon />} sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
                        {t('generate_report_button')}
                    </Button>
                </Box>
            </Box>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 5 } }} ref={reportRef}>
                {/* --- Report Header --- */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{lx('Clinical Assessment Report')}</Typography>
                    <Typography variant="subtitle1" color="text.secondary">{lx('Generated on')}: {new Date().toLocaleDateString()}</Typography>
                </Box>

                {/* --- Child Information --- */}
                <Section title={lx('Child Information')}>
                    <Grid container spacing={2}>
                        <InfoItem label={lx('Name')} value={child_info.name} />
                        <InfoItem label={lx('Latest Assessment Date')} value={latestAssessment.created_at ? new Date(latestAssessment.created_at).toLocaleDateString() : 'N/A'} />
                        <InfoItem label={lx('Joining Date')} value={child_info.joining_date ? new Date(child_info.joining_date).toLocaleDateString() : 'N/A'} />
                        <InfoItem label={lx('Assessed by')} value={isSpeechLatest ? lx('Speech Therapist') : isOTLatest ? lx('OT Specialist') : lx('Clinical Team')} /> 
                        <InfoItem label={lx('Latest Result')} value={<Chip label={(() => {
                            let text = latestAssessment.interpretation;
                            if (text === "0" || !text) {
                                if (isInitialLatest) {
                                    const p = Math.round(((Number(latestAssessment.social_communication_score || 0) + Number(latestAssessment.repetitive_behavior_score || 0)) / 72) * 100);
                                    text = p > 50 ? 'High Probability' : 'Risk Assessment Completed';
                                } else {
                                    text = 'Completed';
                                }
                            }
                            return lx(text);
                        })()} color="primary" sx={{ fontWeight: 'bold' }} />} />
                    </Grid>
                </Section>
                
                {/* --- Executive Summary --- */}
                <Section title={lx('Executive Summary')}>
                     <Typography variant="body1">
                        {reportLang === 'te' ? 
                            `${child_info.name}, మదింపులలో గమనించిన ప్రవర్తనలను ప్రదర్శిస్తున్నారు. ఈ ఫలితాలు తదుపరి చికిత్సా పద్ధతులకు ఆధారం. ఈ నివేదిక పిల్లల అభివృద్ధిని తెలుసుకోవడానికి సహాయపడుతుంది.` :
                            `${child_info.name}, presents with developmental behaviors noted in recent assessments. Assessment results inform the targeted therapy and occupational activities outlined below. This report is a foundation for tracking progress.`
                        }
                        <br/><br/>
                        {reportLang === 'te' ? 
                            "ఈ నివేదిక సమగ్ర అభివృద్ధికి మరియు నిరంతర పురోగతికి మార్గదర్శకంగా పనిచేస్తుంది. ప్రతి విభాగం వివిధ నైపుణ్యాలను మరియు అవసరాలను అంచనా వేస్తుంది." :
                            "This comprehensive document serves as a guided roadmap to ensure continuous development. Each section analyzes specific skill sets and potential areas of intervention. The interdisciplinary approach targets long-lasting positive transformations in the child's daily quality of life."
                        }
                        {isOTLatest && latestAnswers?.generalObservation && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                                <strong>{reportLang === 'te' ? 'సాధారణ పరిశీలన' : 'General Observation'}: </strong>
                                {latestAnswers.generalObservation}
                            </Box>
                        )}
                    </Typography>
                </Section>

                {/* --- Assessment Summary & Risk Profile (Current Selected) --- */}
                {isInitialLatest && (
                    <Section title={lx('Assessment Summary & Risk Profile')}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%', bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('SRS SCORE')}</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{latestAssessment.srs_score || 'N/A'}</Typography>
                                    <Typography variant="caption" color="text.secondary">Social Responsiveness Scale</Typography>
                                    
                                    <Divider sx={{ my: 2 }} />
                                    
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('M-CHAT-R SCORE')}</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{latestAssessment.mchat_score || 'N/A'}</Typography>
                                    <Typography variant="caption" color="text.secondary">Modified Checklist for Autism</Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%', bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('Autism Probability Index')}</Typography>
                                    <Box sx={{ width: '100%', height: 180 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[
                                                        { name: 'Traits', value: Math.round(((Number(latestAssessment.social_communication_score || 0) + Number(latestAssessment.repetitive_behavior_score || 0)) / 72) * 100), color: '#ef4444' },
                                                        { name: 'NT', value: 100 - Math.round(((Number(latestAssessment.social_communication_score || 0) + Number(latestAssessment.repetitive_behavior_score || 0)) / 72) * 100), color: '#10b981' }
                                                    ]}
                                                    cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value"
                                                >
                                                    <Cell fill="#ef4444" />
                                                    <Cell fill="#10b981" />
                                                </Pie>
                                                <RechartsTooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {Math.round(((Number(latestAssessment.social_communication_score || 0) + Number(latestAssessment.repetitive_behavior_score || 0)) / 72) * 100)}%
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('INTERPRETATION')}</Typography>
                                    <Chip 
                                        label={latestAssessment.interpretation || 'N/A'} 
                                        color={getPriorityColor(latestAssessment.interpretation?.toUpperCase())} 
                                        sx={{ fontWeight: 'bold', mb: 2, width: '100%' }} 
                                    />
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        {reportLang === 'te' ? 
                                            "ఈ వివరణ సామాజిక, పునరావృత మరియు అభిజ్ఞా డొమైన్‌లలో నివేదించబడిన ప్రవర్తనల అల్గోరిథం ఆధారిత విశ్లేషణపై ఆధారపడి ఉంటుంది." :
                                            "This interpretation is based on algorithm-driven analysis of reported behaviors across social, repetitive, and cognitive domains."
                                        }
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('Domain Specific Analysis')}</Typography>
                                    <Grid container spacing={4}>
                                        {[
                                            { label: lx("Social"), score: latestAssessment.social_communication_score, max: 40, color: 'primary' },
                                            { label: lx("Repetitive"), score: latestAssessment.repetitive_behavior_score, max: 32, color: 'secondary' },
                                            { label: lx("Inattention"), score: latestAssessment.inattention_score, max: 28, color: 'warning' },
                                            { label: lx("Hyperactivity"), score: latestAssessment.hyperactivity_score, max: 24, color: 'error' },
                                            { label: lx("Speech"), score: latestAssessment.speech_score, max: 64, color: 'success' }
                                        ].map((d, i) => (
                                            <Grid item xs={12} sm={2.4} key={i}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{d.label}</Typography>
                                                    <Typography variant="caption">{d.score}/{d.max}</Typography>
                                                </Box>
                                                <Box sx={{ height: 6, width: '100%', bgcolor: alpha(theme.palette[d.color].main, 0.1), borderRadius: 3 }}>
                                                    <Box sx={{ height: '100%', width: `${(d.score / d.max) * 100}%`, bgcolor: `${d.color}.main`, borderRadius: 3 }} />
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Section>
                )}

                {isSpeechLatest && (
                    <Section title={lx('Speech & Language Re-assessment Summary')}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%', bgcolor: '#f1f8f9' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('TOTAL SCORE')}</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#0097a7' }}>{latestAnswers.summary?.totalScore || 'N/A'}</Typography>
                                    <Typography variant="body2" color="text.secondary">{lx('Improvement')}: {vt(latestAnswers.summary?.improvement)}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={8}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('Clinical Interpretation')}</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{vt(latestAssessment.interpretation)}</Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{latestAnswers.summary?.therapistObservation}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>{lx('Domain Detailed Scoring')}</Typography>
                            <Grid container spacing={2}>
                                {latestAnswers && Object.entries(latestAnswers).filter(([key]) => !['summary', 'parentFeedback'].includes(key)).map(([key, section]) => (
                                    <Grid item xs={12} sm={6} md={4} key={key}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textTransform: 'capitalize', color: 'primary.main' }}>
                                                {lx(key.replace(/([A-Z])/g, ' $1').trim())}
                                            </Typography>
                                            <Divider sx={{ my: 1 }} />
                                            {typeof section === 'object' && Object.entries(section).filter(([k]) => k !== 'comment').map(([skill, score]) => (
                                                <Box key={skill} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{lx(skill.replace(/([A-Z])/g, ' $1').trim())}</Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{score}</Typography>
                                                </Box>
                                            ))}
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ mt: 4, p: 3, bgcolor: '#fff9c4', borderRadius: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>{lx('Therapy Goals & Home Plan')}</Typography>
                            <Typography variant="body2"><strong>{lx('Short-Term Goals:')}</strong> {latestAnswers.summary?.shortTermGoals}</Typography>
                            <Typography variant="body2"><strong>{lx('Long-Term Goals:')}</strong> {latestAnswers.summary?.longTermGoals}</Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2"><strong>{lx('Recommended Activities:')}</strong> {latestAnswers.summary?.recommendedActivities}</Typography>
                            <Typography variant="body2"><strong>{lx('Frequency:')}</strong> {latestAnswers.summary?.frequency}</Typography>
                        </Box>
                    </Section>
                )}

                {/* --- Initial Diagnostic Baseline (Always shown if OT is latest or if multiple assessments) --- */}
                {isOTLatest && firstAssessment && firstAssessment.assessment_type === 'initial' && (
                    <Section title={lx('Initial Diagnostic Baseline')}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%', bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('SRS SCORE')}</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>{firstAssessment.srs_score || 'N/A'}</Typography>
                                    <Typography variant="caption" color="text.secondary">Social Responsiveness Scale</Typography>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('M-CHAT-R SCORE')}</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{firstAssessment.mchat_score || 'N/A'}</Typography>
                                    <Typography variant="caption" color="text.secondary">Modified Checklist for Autism</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%', bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('Autism Probability Index')}</Typography>
                                    <Box sx={{ width: '100%', height: 180 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={[
                                                        { name: 'Traits', value: Math.round(((Number(firstAssessment.social_communication_score || 0) + Number(firstAssessment.repetitive_behavior_score || 0)) / 72) * 100), color: '#ef4444' },
                                                        { name: 'NT', value: 100 - Math.round(((Number(firstAssessment.social_communication_score || 0) + Number(firstAssessment.repetitive_behavior_score || 0)) / 72) * 100), color: '#10b981' }
                                                    ]}
                                                    cx="50%" cy="50%" innerRadius={40} outerRadius={60} paddingAngle={5} dataKey="value"
                                                >
                                                    <Cell fill="#ef4444" />
                                                    <Cell fill="#10b981" />
                                                </Pie>
                                                <RechartsTooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        {Math.round(((Number(firstAssessment.social_communication_score || 0) + Number(firstAssessment.repetitive_behavior_score || 0)) / 72) * 100)}%
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, height: '100%', bgcolor: '#fcfcfc' }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{lx('INITIAL INTERPRETATION')}</Typography>
                                    <Chip 
                                        label={firstAssessment.interpretation || 'N/A'} 
                                        color={getPriorityColor(firstAssessment.interpretation?.toUpperCase())} 
                                        sx={{ fontWeight: 'bold', mb: 2, width: '100%' }} 
                                    />
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                        {reportLang === 'te' ? 
                                            "మొదటి మదింపు సమయంలో నమోదు చేయబడిన ఫలితాలు ఇవి." :
                                            "These are the baseline diagnostic results recorded during the first initial assessment."
                                        }
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Section>
                )}

                {/* --- Initial Assessment Extracted (Includes 1st Questionnaire) --- */}
                {firstAssessment && firstAssessment.assessment_type === 'initial' && Array.isArray(firstAnswers) && (
                    <Section title={lx('Initial Assessment Questionnaire')}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {reportLang === 'te' ? "కింద ఇవ్వబడినవి మొదట్లో అంచనా వేసిన ముఖ్యమైన ప్రశ్నలు మరియు వాటి జవాబులు." : "Below are the responses recorded during the initial Assessment."}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            {firstAnswers.map((answerIndex, qIdx) => {
                                if (answerIndex === null || answerIndex === undefined) return null;
                                return (
                                    <Box key={qIdx} sx={{ 
                                        display: 'flex', 
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        justifyContent: 'space-between',
                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                        py: 1.5,
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        gap: 1
                                    }}>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 24, color: 'text.secondary' }}>
                                                {qIdx + 1}.
                                            </Typography>
                                            <Typography variant="body2">
                                                {questionsList[qIdx]}
                                            </Typography>
                                        </Box>
                                        <Chip 
                                            size="small" 
                                            label={answerLabelsList[answerIndex]} 
                                            color={answerIndex > 2 ? 'warning' : 'default'}
                                            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, minWidth: 80 }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Section>
                )}

                {/* --- In-Depth OT Breakdown (if latest is OT Reassessment) --- */}
                {isOTLatest && latestAnswers && latestAnswers.physicalAssessment && (
                    <Section title={lx('Occupational Therapy Reassessment Details')}>
                        <Grid container spacing={4}>
                            {/* Physical Assessment */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Physical & Postural Assessment')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary="Muscle Tone" secondary={`Upper: ${latestAnswers.physicalAssessment.muscleToneUpper} | Lower: ${latestAnswers.physicalAssessment.muscleToneLower}`} /></ListItem>
                                    <ListItem><ListItemText primary="Equilibrium & Posture" secondary={`Equilibrium: ${latestAnswers.physicalAssessment.equilibrium} | Postural Tone: ${latestAnswers.physicalAssessment.posturalTone}`} /></ListItem>
                                    <ListItem><ListItemText primary="Reflex Integration" secondary={`ATNR: ${latestAnswers.physicalAssessment.atnr} | STNR: ${latestAnswers.physicalAssessment.stnr} | TLR: ${latestAnswers.physicalAssessment.tlr}`} /></ListItem>
                                    <ListItem><ListItemText primary="Tightness / Deformities" secondary={`Upper: ${latestAnswers.physicalAssessment.tightnessUpper} | Lower: ${latestAnswers.physicalAssessment.tightnessLower} | Deformities: ${latestAnswers.physicalAssessment.deformities}`} /></ListItem>
                                    <ListItem><ListItemText primary="Voluntary Control" secondary={`Upper: ${latestAnswers.physicalAssessment.voluntaryUpper} | Lower: ${latestAnswers.physicalAssessment.voluntaryLower}`} /></ListItem>
                                </List>
                            </Grid>

                            {/* Motor Development */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Motor Development')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary="Fine Motor Coordination" secondary={latestAnswers.fineMotor?.coordination} /></ListItem>
                                    <ListItem><ListItemText primary="Unilateral / Bilateral" secondary={`Unilateral: ${latestAnswers.fineMotor?.unilateral} | Bilateral: ${latestAnswers.fineMotor?.bilateral}`} /></ListItem>
                                    <ListItem><ListItemText primary="Eye-Hand Coordination" secondary={latestAnswers.fineMotor?.eyeHand} /></ListItem>
                                    <ListItem><ListItemText primary="Gross Motor Status" secondary={latestAnswers.grossMotor?.status} /></ListItem>
                                </List>
                            </Grid>

                            {/* Hand Function */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Hand Function')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Typography variant="subtitle2" sx={{ mt: 1, ml: 2, fontWeight: 'bold' }}>{lx('Non-Prehensile')}</Typography>
                                <List dense sx={{ ml: 2 }}>
                                    <ListItem><ListItemText primary={lx("Pushing")} secondary={vt(latestAnswers.nonPrehensile?.pushingAfter)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Pulling")} secondary={vt(latestAnswers.nonPrehensile?.pullingAfter)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Clapping")} secondary={vt(latestAnswers.nonPrehensile?.clappingAfter)} /></ListItem>
                                </List>
                                <Typography variant="subtitle2" sx={{ mt: 1, ml: 2, fontWeight: 'bold' }}>{lx('Prehensile (Grasps)')}</Typography>
                                <List dense sx={{ ml: 2 }}>
                                    <ListItem><ListItemText primary={lx("Cylindrical")} secondary={`${lx('Rt')}: ${vt(latestAnswers.prehensile?.cylindricalRt)} | ${lx('Lf')}: ${vt(latestAnswers.prehensile?.cylindricalLf)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Spherical")} secondary={`${lx('Rt')}: ${vt(latestAnswers.prehensile?.sphericalRt)} | ${lx('Lf')}: ${vt(latestAnswers.prehensile?.sphericalLf)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Pincer")} secondary={`${lx('Rt')}: ${vt(latestAnswers.prehensile?.pincerRt)} | ${lx('Lf')}: ${vt(latestAnswers.prehensile?.pincerLf)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Beading / Scissoring")} secondary={`${lx('Beading')}: ${vt(latestAnswers.prehensile?.beading)} | ${lx('Scissoring')}: ${vt(latestAnswers.prehensile?.scissoring)}`} /></ListItem>
                                </List>
                            </Grid>

                            {/* Behavioral & Attention */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Behavioral & Attention')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Command Following")} secondary={vt(latestAnswers.behavioralAttention?.commandFollowing)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Span of Reaction")} secondary={vt(latestAnswers.behavioralAttention?.spanOfReaction)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Attention")} secondary={vt(latestAnswers.behavioralAttention?.attention)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Hyperactivity")} secondary={vt(latestAnswers.behavioralAttention?.hyperactivity)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Verbal Stimming")} secondary={vt(latestAnswers.behavioralAttention?.verbalStimming)} /></ListItem>
                                </List>
                            </Grid>

                            {/* Positions & Transitions */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Positions & Transitions')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Walking / Running")} secondary={vt(latestAnswers.positionTransitions?.walkingAfter)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Standing (One Leg)")} secondary={vt(latestAnswers.positionTransitions?.standingAfter)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Stairs / Balance Beam")} secondary={`${lx('Stairs')}: ${vt(latestAnswers.positionTransitions?.stairsAfter)} | ${lx('Beam')}: ${vt(latestAnswers.positionTransitions?.balanceBeamAfter)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Ball Skills")} secondary={`${lx('Catch')}: ${vt(latestAnswers.positionTransitions?.catchBallAfter)} | ${lx('Over Head')}: ${vt(latestAnswers.positionTransitions?.throwBallAfter)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Ride Cycle")} secondary={vt(latestAnswers.positionTransitions?.rideCycleAfter)} /></ListItem>
                                </List>
                            </Grid>

                            {/* Self Care & Daily Living */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Self Care & Daily Living')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Eating")} secondary={vt(latestAnswers.selfCare?.eating)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Bathing")} secondary={vt(latestAnswers.selfCare?.bathing)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Dressing (Upper / Lower)")} secondary={`${lx('Upper')}: ${vt(latestAnswers.selfCare?.dressingUpper)} | ${lx('Lower')}: ${vt(latestAnswers.selfCare?.dressingLower)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Toileting / Buttoning")} secondary={`${lx('Toileting')}: ${vt(latestAnswers.selfCare?.toileting)} | ${lx('Buttoning')}: ${vt(latestAnswers.selfCare?.buttoning)}`} /></ListItem>
                                </List>
                            </Grid>

                            {/* Writing Skills */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Writing Skills')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Grips")} secondary={`${lx('Pincer')}: ${vt(latestAnswers.writingSkills?.pincerGrip)} | ${lx('Finger')}: ${vt(latestAnswers.writingSkills?.fingerGripping)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Alphabets")} secondary={vt(latestAnswers.writingSkills?.writingAlphabets)} /></ListItem>
                                </List>
                            </Grid>

                            {/* Preferences */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="primary">{lx('Preferences')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Sensory")} secondary={vt(latestAnswers.preferences?.sensory)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Food")} secondary={vt(latestAnswers.preferences?.food)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Play")} secondary={vt(latestAnswers.preferences?.play)} /></ListItem>
                                </List>
                            </Grid>
                        </Grid>
                    </Section>
                )}

                {/* --- In-Depth Behavioral Breakdown --- */}
                {isBehavioralLatest && latestAnswers && (
                    <Section title={lx('Behavioral Reassessment Details')}>
                        <Grid container spacing={4}>
                            {/* Basics */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#673ab7">{lx('Attention & Compliance')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Sustains Attention")} secondary={vt(latestAnswers.attentionSitting?.sustainsAttentionAfter)} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Sitting Tolerance")} secondary={`${latestAnswers.attentionSitting?.sittingToleranceAfter} ${lx('mins')}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Follows Instructions")} secondary={`${lx('Simple')}: ${vt(latestAnswers.compliance?.simpleInstructionsAfter)} | ${lx('2-Step')}: ${vt(latestAnswers.compliance?.twoStepInstructionsAfter)}`} /></ListItem>
                                </List>
                            </Grid>

                            {/* Emotional & Aggressive */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#673ab7">{lx('Emotional & Behavioral Control')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Emotional Regulation")} secondary={`${lx('Tantrums')}: ${vt(latestAnswers.emotionalRegulation?.tantrumsAfter)} | ${lx('Crying')}: ${vt(latestAnswers.emotionalRegulation?.cryingSpellsAfter)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Aggressive Behavior")} secondary={`${lx('Hits/Kicks')}: ${vt(latestAnswers.aggressiveBehavior?.hittingKickingAfter)} | ${lx('Biting')}: ${vt(latestAnswers.aggressiveBehavior?.bitingAfter)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Self-Harm")} secondary={vt(latestAnswers.aggressiveBehavior?.selfHarmAfter)} /></ListItem>
                                </List>
                            </Grid>

                            {/* Sensory & Repetitive */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#673ab7">{lx('Sensory & Repetitive')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Repetitive Behavior")} secondary={`${lx('Flapping')}: ${vt(latestAnswers.repetitiveBehavior?.handFlappingAfter)} | ${lx('Stemming')}: ${vt(latestAnswers.repetitiveBehavior?.verbalStemmingAfter)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Sensory (Sound/Touch)")} secondary={`${lx('Sound')}: ${vt(latestAnswers.sensoryRelated?.soundAfter)} | ${lx('Touch')}: ${vt(latestAnswers.sensoryRelated?.touchAfter)}`} /></ListItem>
                                </List>
                            </Grid>

                            {/* Social & Transitions */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#673ab7">{lx('Social & Transitions')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary={lx("Social")} secondary={`${lx('Eye Contact')}: ${vt(latestAnswers.socialBehavior?.eyeContactAfter)} | ${lx('Name')}: ${vt(latestAnswers.socialBehavior?.respondsToNameAfter)}`} /></ListItem>
                                    <ListItem><ListItemText primary={lx("Transitions")} secondary={vt(latestAnswers.transitionsFlexibility?.difficultyWithTransitionsAfter)} /></ListItem>
                                </List>
                            </Grid>

                            {/* Skills Labeling (Tact) */}
                            <Grid item xs={12}>
                                <Typography variant="h6" color="#673ab7">{lx('Tact Skills (Labeling) Progress')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Grid container spacing={2}>
                                    <Grid item xs={6} md={2.4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{lx('Objects')}</Typography>
                                        <Typography variant="caption" display="block">{lx('Common')}: {vt(latestAnswers.tactSkills?.objectLabeling?.commonObjectAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Personal')}: {vt(latestAnswers.tactSkills?.objectLabeling?.personalItemAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Classroom')}: {vt(latestAnswers.tactSkills?.objectLabeling?.classroomItemAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Household')}: {vt(latestAnswers.tactSkills?.objectLabeling?.householdItemAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Toys')}: {vt(latestAnswers.tactSkills?.objectLabeling?.toysAfter)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={2.4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{lx('Actions')}</Typography>
                                        <Typography variant="caption" display="block">{lx('Running', true)}: {vt(latestAnswers.tactSkills?.actionLabeling?.runningAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Eating', true)}: {vt(latestAnswers.tactSkills?.actionLabeling?.eatingAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Sleeping', true)}: {vt(latestAnswers.tactSkills?.actionLabeling?.sleepingAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Jumping', true)}: {vt(latestAnswers.tactSkills?.actionLabeling?.jumpingAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Playing', true)}: {vt(latestAnswers.tactSkills?.actionLabeling?.playingAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Sitting', true)}: {vt(latestAnswers.tactSkills?.actionLabeling?.sittingAfter)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={2.4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{lx('Categories')}</Typography>
                                        <Typography variant="caption" display="block">{lx('Animals', true)}: {vt(latestAnswers.tactSkills?.categoryLabeling?.animalsAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Fruits', true)}: {vt(latestAnswers.tactSkills?.categoryLabeling?.fruitsAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Vegetables', true)}: {vt(latestAnswers.tactSkills?.categoryLabeling?.vegetablesAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Occupation', true)}: {vt(latestAnswers.tactSkills?.categoryLabeling?.occupationAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Body Parts', true)}: {vt(latestAnswers.tactSkills?.categoryLabeling?.bodyPartsAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Vehicle', true)}: {vt(latestAnswers.tactSkills?.categoryLabeling?.vehicleAfter)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={2.4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{lx('Colors')}</Typography>
                                        <Typography variant="caption" display="block">{lx('Red', true)}: {vt(latestAnswers.tactSkills?.colorLabeling?.redAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Blue', true)}: {vt(latestAnswers.tactSkills?.colorLabeling?.blueAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Green', true)}: {vt(latestAnswers.tactSkills?.colorLabeling?.greenAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Yellow', true)}: {vt(latestAnswers.tactSkills?.colorLabeling?.yellowAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Other', true)}: {vt(latestAnswers.tactSkills?.colorLabeling?.otherColoursAfter)}</Typography>
                                    </Grid>
                                    <Grid item xs={6} md={2.4}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{lx('Academic')}</Typography>
                                        <Typography variant="caption" display="block">{lx('Alphabet')}: {vt(latestAnswers.tactSkills?.alphabetNumberLabeling?.alphabetAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Numbers')}: {vt(latestAnswers.tactSkills?.alphabetNumberLabeling?.numberAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Telugu')}: {vt(latestAnswers.tactSkills?.alphabetNumberLabeling?.teluguAfter)}</Typography>
                                        <Typography variant="caption" display="block">{lx('Shapes')}: {vt(latestAnswers.tactSkills?.alphabetNumberLabeling?.shapesAfter)}</Typography>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Other Skills */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#673ab7">{lx('Imitation & Play')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary="Imitation" secondary={`Motor: ${latestAnswers.imitationSkills?.motorImitation} | Verbal: ${latestAnswers.imitationSkills?.verbalImitation}`} /></ListItem>
                                    <ListItem><ListItemText primary="Play" secondary={`Functional: ${latestAnswers.playSocialSkills?.functionalPlay} | Pretend: ${latestAnswers.playSocialSkills?.pretendPlay}`} /></ListItem>
                                </List>
                            </Grid>

                             {/* Communication */}
                             <Grid item xs={12} md={6}>
                                <Typography variant="h6" color="#673ab7">{lx('Communication')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <List dense>
                                    <ListItem><ListItemText primary="Comprehension" secondary={latestAnswers.communicationSkills?.comprehensionAfter} /></ListItem>
                                    <ListItem><ListItemText primary="Expression" secondary={latestAnswers.communicationSkills?.expressionAfter} /></ListItem>
                                </List>
                            </Grid>

                            {/* Summaries */}
                            <Grid item xs={12}>
                                <Typography variant="h6" color="#673ab7">{lx('Clinical Summaries')}</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                                    <Typography variant="body2"><strong>Strengths:</strong> {latestAnswers.summaries?.strengths}</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Areas of Need:</strong> {latestAnswers.summaries?.needs}</Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Overall Summary:</strong> {latestAnswers.summaries?.overallSummary}</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Section>
                )}

                {/* --- Adding length to the report to make it 4-5 pages --- */}
                <Section title={lx('Comprehensive Developmental Strategy & Road Map')}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {reportLang === 'te' ? 
                            "పిల్లల వయస్సు, ప్రస్తుత పనితీరు మరియు మదింపు ఫలితాల ఆధారంగా, సమగ్ర అభివృద్ధి వ్యూహాన్ని రూపొందించాము. రోజువారీ జీవిత నైపుణ్యాలు అభ్యసించడం, నిరంతర చికిత్స, మరియు ప్రవర్తనా మద్దతు అత్యవసరం." :
                            "Based on the child's age, baseline functional capacity, and the empirical data synthesized from both initial and reassessment workflows, a highly comprehensive roadmap has been charted. Developmental alignment is an ongoing process."
                        }
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {reportLang === 'te' ?
                            "ముందుగా చెప్పినట్లుగా, సామాజిక సంభాషణ నైపుణ్యాలను మరియు శ్రద్ధను మెరుగుపరచడం చాలా ముఖ్యం. మోటార్ నైపుణ్యాలను అభివృద్ధి చేయడం ద్వారా పిల్లవాడు తన పనులను తాను చేసుకునే సామర్థ్యాన్ని పెంచవచ్చు." :
                            "As detailed in the earlier sections of this report, enhancing social communicative intent and sustained attention are foundational milestones. By further scaffolding prehensile strategies and gross motor resilience, the child will likely experience elevated autonomous functional capabilities."
                        }
                    </Typography>
                    <Grid container spacing={2}>
                        {(!child_info.recommended_therapies || child_info.recommended_therapies.length === 0) && (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                                    <Typography variant="body2">{lx('No specific therapies recommended yet. General developmental support is advised.')}</Typography>
                                </Paper>
                            </Grid>
                        )}
                        {child_info.recommended_therapies?.includes('occupational') && (
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#fbfbfb', borderLeft: '4px solid #00bcd4' }}>
                                    <Typography variant="h6">{lx('Occupational Therapy Roadmap')}</Typography>
                                    <Typography variant="body2">
                                        {reportLang === 'te' ? 
                                            "జ్ఞానేంద్రియ అనుసంధానం మరియు మోటార్ నైపుణ్యాలపై దృష్టి సారించడం. స్థిరమైన జ్ఞానేంద్రియ ఆహారాన్ని అమలు చేయడం ద్వారా నాడీ వ్యవస్థను నియంత్రించవచ్చు. తీవ్రమైన ఒత్తిడి చికిత్సలు మరియు స్పర్శ ప్రేరణ ద్వారా మొత్తం అవగాహన మరియు స్వతంత్రతను పెంచవచ్చు." :
                                            "Focusing on sensory integration and motor skills. Implementing a consistent sensory diet helps modulate the central nervous system. Using deep pressure therapies and tactile stimulation will elevate overall awareness and ADL independence."
                                        }
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                        {child_info.recommended_therapies?.includes('behavioral') && (
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#fbfbfb', borderLeft: '4px solid #9c27b0' }}>
                                    <Typography variant="h6">{lx('Behavioral Therapy Roadmap')}</Typography>
                                    <Typography variant="body2">
                                        {reportLang === 'te' ? 
                                            "విధేయత మరియు సామాజిక నిశ్చితార్థాన్ని పెంచడానికి సానుకూల బలపరిచే నమూనాలను ఉపయోగించడం. దృశ్య షెడ్యూల్‌లు మరియు టోకెన్ వ్యవస్థల ద్వారా కార్యకలాపాల మధ్య పరివర్తనకు మద్దతు లభిస్తుంది, ఇది అనవసరమైన ప్రవర్తనలను తగ్గిస్తుంది." :
                                            "Utilizing positive reinforcement models to increase compliance and social engagement. Transitioning between activities will be supported via visual schedules and token economies to reduce maladaptive stimming behaviors."
                                        }
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                        {child_info.recommended_therapies?.includes('speech') && (
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2, bgcolor: '#fbfbfb', borderLeft: '4px solid #ff9800' }}>
                                    <Typography variant="h6">{lx('Speech & Language Roadmap')}</Typography>
                                    <Typography variant="body2">
                                        {reportLang === 'te' ? 
                                            "క్రియాత్మక కమ్యూనికేషన్ మరియు సామాజిక ఉద్దేశ్యంపై లక్ష్యంగా పెట్టుకోవడం. సామాజిక సంకేతాలను అనుకరించడం, వంతులు వేసుకోవడం మరియు ఉమ్మడి శ్రద్ధపై ఈ వ్యూహం దృష్టి సారిస్తుంది." :
                                            "Targeting functional communication and pragmatic social intent. The strategy focuses on mimicking real-world social cues, turn-taking, and joint attention, moving from rote echolalia towards spontaneous dialogue."
                                        }
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2, bgcolor: '#fbfbfb', borderLeft: '4px solid #4caf50' }}>
                                <Typography variant="h6">{lx('General Developmental Strategy')}</Typography>
                                <Typography variant="body2">
                                    {reportLang === 'te' ? 
                                        "క్లినికల్ బృందం మరియు కుటుంబ యూనిట్ మధ్య నిరంతర సహకారం చికిత్సా అనుసంధానాన్ని నిర్ధారిస్తుంది. రెగ్యులర్ మల్టీ-డిసిప్లినరీ సమీక్షలు మైలురాళ్ల సముపార్జనను ట్రాక్ చేస్తాయి." :
                                        "Consistent collaboration between the clinical team and family unit ensures therapeutic alignment. Regular multi-disciplinary reviews will track milestone acquisitions and foster generalization of newly acquired skills."
                                    }
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </Section>

                {/* --- Abstract Assessment Scores (Charts History) --- */}
                <Section title={lx('Assessment Scoring History')}>
                    <Grid container spacing={2}>
                        {assessments.map((assessment, index) => {
                            const isOT = assessment.assessment_type === "ot_reassessment";
                            const isBehavioral = assessment.assessment_type === "behavioral_reassessment";
                            const typeName = isOT ? "Occupational Therapy Reassessment" : 
                                           isBehavioral ? "Behavioral Reassessment" : "Autism & ADHD Assessment";

                            const s1Label = isOT ? "Physical & Postural" : isBehavioral ? "Social Interaction" : "Social Communication";
                            const s2Label = isOT ? "Motor Skills & Transitions" : isBehavioral ? "Repetitive Behavior" : "Repetitive Behavior";
                            const s3Label = isOT ? "Behavior & Attention" : isBehavioral ? "Attention & Focus" : "Inattention";
                            const s4Label = isOT ? "Self Care & ADL" : isBehavioral ? "Compliance & Control" : "Hyperactivity";

                            return (
                             <Grid item xs={12} key={index}>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                    <Typography variant="h6" gutterBottom>{typeName} on {new Date(assessment.created_at).toLocaleDateString()}</Typography>
                                    <Grid container spacing={2}>
                                        <ScoreItem label={s1Label} score={assessment.social_communication_score} />
                                        <ScoreItem label={s2Label} score={assessment.repetitive_behavior_score} />
                                        <ScoreItem label={s3Label} score={assessment.inattention_score} />
                                        <ScoreItem label={s4Label} score={assessment.hyperactivity_score} />
                                        {!isOT && <ScoreItem label={lx("Speech")} score={assessment.speech_score} />}
                                    </Grid>
                                    {assessment.insights && assessment.insights.length > 0 && (
                                        <>
                                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 'bold', mt: 2, display: 'block' }}>{lx('Findings Overview')}</Typography>
                                            <List sx={{ mb: 2 }}>
                                                {assessment.insights.map((insight, insightIndex) => (
                                                    <ListItem key={insightIndex} sx={{ px: 0, py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 32 }}><InfoIcon color="primary" fontSize="small" /></ListItemIcon>
                                                        <ListItemText primary={insight} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </>
                                    )}
                                </Paper>
                             </Grid>
                            );
                        })}
                    </Grid>
                </Section>
                
                <Section title={lx('Detailed Overview & Next Steps')}>
                     <Typography variant="body1" sx={{ mb: 2 }}>
                        {reportLang === 'te' ? "ఈ నివేదిక భవిష్యత్తు చికిత్సా ప్రణాళికకు పునాదిగా ఉంటుంది." : "The cumulative findings in this document highlight the necessity of structured, predictable environments. Continued collaboration between the clinical team and the family unit forms the bedrock of progress."}
                     </Typography>
                     <Typography variant="body1">
                        {reportLang === 'te' ? "వచ్చే వారం తల్లిదండ్రులతో సమీక్షా సమావేశం ఉంటుంది." : "Subsequent multi-disciplinary reviews will track milestone acquisitions. Regular parental consultations guarantees therapeutic alignment within the home setting, fostering generalization of newly acquired skills."}
                     </Typography>
                </Section>

                {/* --- Goals Section --- */}
                <Section title={lx('Goals & Objectives')}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{lx('Short-term Goals')}</Typography>
                    {child_info.recommended_therapies?.includes('behavioral') && (
                        <GoalItem title="Social Interaction & Compliance" target="Increase turn-taking and response to name" outcome="Follow 2-step instructions consistently" priority="HIGH" lx={lx} />
                    )}
                    {child_info.recommended_therapies?.includes('occupational') && (
                        <GoalItem title="Fine Motor & Sensory Regulation" target="Improve pincer grasp and sitting tolerance" outcome="Write/Draw for 10 mins with focused attention" priority="HIGH" lx={lx} />
                    )}
                    {child_info.recommended_therapies?.includes('speech') && (
                        <GoalItem title="Functional Communication" target="Reduce echolalia and use expressive words" outcome="Communicate needs using 3-word sentences" priority="HIGH" lx={lx} />
                    )}
                    {(!child_info.recommended_therapies || child_info.recommended_therapies.length === 0) && (
                        <GoalItem title="General Developmental Milestones" target="Consistent performance in observed domains" outcome="Achieve emerging score levels" priority="MEDIUM" lx={lx} />
                    )}
                    
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>{lx('Long-term Goals')}</Typography>
                    {child_info.recommended_therapies?.includes('behavioral') && (
                        <GoalItem title="Behavioral Independence" target="Function in group settings without one-on-one support" outcome="Socially appropriate behavior in variety of settings" priority="HIGH" lx={lx} />
                    )}
                    {child_info.recommended_therapies?.includes('occupational') && (
                        <GoalItem title="ADL Independence" target="Complete all self-care tasks independently" outcome="Master fine motor control at age-appropriate level" priority="HIGH" lx={lx} />
                    )}
                    {child_info.recommended_therapies?.includes('speech') && (
                        <GoalItem title="Spontaneous Dialogue" target="Carry out complex conversations naturally" outcome="Effective social communication for academic/social success" priority="HIGH" lx={lx} />
                    )}
                    {(!child_info.recommended_therapies || child_info.recommended_therapies.length === 0) && (
                        <GoalItem title="Overall Developmental Alignment" target="Perform tasks independently at age level" outcome="Function in mainstream environments" priority="HIGH" lx={lx} />
                    )}
                </Section>

                {/* --- Recommendations Section --- */}
                <Section title={lx('Recommendations')}>
                    {child_info.recommended_therapies?.includes('occupational') && (
                        <RecommendationItem title="Occupational Interventions" urgency="IMMEDIATE" actions={['Sensory integration activities', 'Targeted fine and gross motor skill development', 'Self-care independence practice']} lx={lx} />
                    )}
                    {child_info.recommended_therapies?.includes('behavioral') && (
                        <RecommendationItem title="Behavioral Support" urgency="IMMEDIATE" actions={['Positive reinforcement schedules', 'Social skills training groups', 'Environmental modifications for stability']} lx={lx} />
                    )}
                    {child_info.recommended_therapies?.includes('speech') && (
                        <RecommendationItem title="Speech & Language" urgency="IMMEDIATE" actions={['Functional communication training', 'Joint attention exercises', 'Augmentative communication if necessary']} lx={lx} />
                    )}
                    <RecommendationItem title={lx('Parent Training and Support')} urgency="IMMEDIATE" actions={['Learn interactive reinforcement', 'Implement structured routines at home', 'Engage in parent-coached play sessions']} lx={lx} />
                </Section>
                 
            </Paper>
        </Container>
    );
};

// --- Sub-components for a cleaner structure ---

const Section = ({ title, children }) => (
    <Box sx={{ mb: 4, pageBreakInside: 'avoid' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, borderBottom: '2px solid #1976d2', pb: 1 }}>{title}</Typography>
        {children}
    </Box>
);

const InfoItem = ({ label, value }) => (
    <Grid item xs={12} sm={6} md={3}>
        <Typography variant="body1" color="text.secondary">{label}:</Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{value}</Typography>
    </Grid>
);

const ScoreItem = ({ label, score }) => (
     <Grid item xs={6} sm={3} sx={{ textAlign: 'center', minHeight: '100px' }}>
        <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: 'primary.dark', 
            fontSize: { xs: '1.5rem', sm: '2rem' } 
        }}>{score || '0'}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>{label}</Typography>
    </Grid>
);

const GoalItem = ({ title, target, outcome, priority, lx }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{lx ? lx(title) : title}</Typography>
            <Chip label={priority} color={getPriorityColor(priority)} size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <strong>{lx ? lx('Target') : 'Target'}:</strong> {lx ? lx(target) : target} | <strong>{lx ? lx('Outcome') : 'Outcome'}:</strong> {lx ? lx(outcome) : outcome}
        </Typography>
    </Paper>
);

const RecommendationItem = ({ title, urgency, actions, lx }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">{lx ? lx(title) : title}</Typography>
            <Chip label={urgency} color={urgency === 'IMMEDIATE' ? 'error' : 'warning'} />
        </Box>
        <List dense>
            {actions.map((action, index) => (
                <ListItem key={index} sx={{ p: 0 }}>
                    <ListItemIcon sx={{ minWidth: '32px' }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={lx ? lx(action) : action} />
                </ListItem>
            ))}
        </List>
    </Paper>
);

export default ChildReport;